/**
 * The push service worker (`public/firebase-messaging-sw.js`), seen from the page.
 *
 * It lives in its own scope so it never competes with the app shell's worker at `/`; push events
 * and notification clicks reach it all the same. It is built by nobody: what it needs from the
 * build (API URL, version) and from the brand (URL allowlist) travels in its query string, so a
 * change of any of them is a new script and the browser updates it.
 *
 * The worker talks to the page with `postMessage`: `RECEIVED` for a push that arrives while a tab
 * is visible (the page refreshes its unread count) and `OPENED` for a click on a notification it
 * showed.
 * A click with no tab open opens the site with the notification in `OPEN_PARAM`, which
 * `captureLaunch` takes off the address bar before the router reads it.
 */
export const SCOPE_PATH = '/firebase-cloud-messaging-push-scope';
export const SCRIPT_PATH = '/firebase-messaging-sw.js';
export const MESSAGES = Object.freeze({
  RECEIVED: 'notifications_v2:received',
  OPENED: 'notifications_v2:opened',
});
export const OPEN_PARAM = 'nv2_open';

const log = (...args) => console.log('[NotificationsV2][worker]', ...args);

const publicUrl = () => process.env.PUBLIC_URL || '';

export const scriptUrl = (config = {}) => {
  const params = new URLSearchParams({
    api: process.env.REACT_APP_API_URL || '',
    hosts: (config.url_allowlist || []).join(','),
    v: process.env.REACT_APP_VERSION || '',
  });
  return `${publicUrl()}${SCRIPT_PATH}?${params.toString()}`;
};

/** Resolves once the worker can hold a push subscription (an active worker is required). */
const whenActive = registration =>
  new Promise(resolve => {
    if (registration.active) {
      resolve(registration);
      return;
    }
    const worker = registration.installing || registration.waiting;
    if (!worker) {
      resolve(registration);
      return;
    }
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated' || worker.state === 'redundant') {
        resolve(registration);
      }
    });
  });

let registered = { url: null, promise: null };

/** Registers (or updates) the push worker for this config. One registration per script URL. */
export const register = (config = {}) => {
  const url = scriptUrl(config);

  if (registered.url !== url || !registered.promise) {
    registered = {
      url,
      promise: navigator.serviceWorker
        .register(url, {
          scope: `${publicUrl()}${SCOPE_PATH}`,
          updateViaCache: 'none',
        })
        .then(whenActive)
        .catch(err => {
          registered = { url: null, promise: null };
          throw err;
        }),
    };
  }

  return registered.promise;
};

/**
 * Listens for one kind of message from the worker. Returns the unsubscribe function. The page is
 * not controlled by the push worker (different scope), and messages from it still arrive here.
 */
export const onMessage = (type, listener) => {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return () => {};
  }

  const handler = event => {
    if (event?.data?.type === type) {
      listener(event.data.payload);
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);
  navigator.serviceWorker.startMessages?.();

  return () => navigator.serviceWorker.removeEventListener('message', handler);
};

let launch = null;

/**
 * The notification click that opened this page, taken off the address bar so it is handled once
 * and never ends up in a bookmark, a redirect or the login's `?path=`. Call it before the first
 * render: the router reads the location when it mounts.
 */
export const captureLaunch = () => {
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(OPEN_PARAM);
    if (!raw) {
      return null;
    }

    url.searchParams.delete(OPEN_PARAM);
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );

    const parsed = JSON.parse(raw);
    launch = parsed && typeof parsed === 'object' ? parsed : null;
  } catch (err) {
    log('cannot read the launch notification', err?.message);
    launch = null;
  }

  return launch;
};

/** The launch notification, once. */
export const takeLaunch = () => {
  const value = launch;
  launch = null;
  return value;
};

/**
 * OneSignal's workers took the root scope and kept a push subscription of their own. Nothing serves
 * their scripts any more, but a registered worker outlives its script, so they are removed here.
 */
export const unregisterLegacyWorkers = async () => {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistrations) {
    return 0;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const legacy = registrations.filter(r =>
      /OneSignalSDK/i.test(
        (r.active || r.waiting || r.installing)?.scriptURL || '',
      ),
    );
    await Promise.all(legacy.map(r => r.unregister()));
    if (legacy.length) {
      log('removed OneSignal workers', legacy.length);
    }
    return legacy.length;
  } catch (err) {
    log('cannot list workers', err?.message);
    return 0;
  }
};

/** Tests only. */
export const __reset = () => {
  registered = { url: null, promise: null };
  launch = null;
};
